import { Box, Card, CardContent, Divider, Skeleton, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { FlowTriggerKind, FlowTriggerResponsePayload } from 'flow-schema';
import _ from 'lodash';
import { Link } from 'react-router-dom';

import { Alert, Text, api, getQueryProps } from 'common';

import OrganizationLabel from '../../../../../../../components/Label/Organization';
import { formatPhoneNumber } from '../../../../../../../utils/string';

interface TriggersProps {
  flowId: string;
}

export function Triggers(props: TriggersProps) {
  const { flowId } = props;
  const {
    data: triggers,
    isSuccess,
    isLoading,
    error,
  } = useQuery({
    ...getQueryProps(api.client.Triggers, 'findAll', {
      flowId,
      payloadType: FlowTriggerResponsePayload.ParentTableAndAllChildTables,
    }),
  });

  if (isLoading) {
    return <Skeleton variant="rectangular" height={80} />;
  }

  if (!isSuccess) {
    return <Alert title="Unable to load triggers" variant="error" error={error} />;
  }

  if (!triggers?.length) {
    return;
  }

  return (
    <Stack spacing={2}>
      {triggers.map((trigger) => {
        const { id, kind, organizationId } = trigger;
        if (kind === FlowTriggerKind.VoiceCall) {
          const phoneNumber = trigger.phoneNumber;
          return (
            <Card key={id} variant="outlined">
              <CardContent>
                <Stack gap={1}>
                  <Text variant="body2">Triggering on end of call events from:</Text>
                  <Text variant="body2">
                    <Link to={`/phone-numbers?phoneNumber=${phoneNumber}`}>
                      {formatPhoneNumber(phoneNumber)}
                    </Link>
                  </Text>
                </Stack>
              </CardContent>
            </Card>
          );
        }
        const santizedTrigger = _.pick(trigger, [
          'messageType',
          'messageTrigger',
          'sendingFacility',
          'interchangeSenderId',
          'transactionSetIdCode',
          'transactionSetPurposeCode',
          'fileType',
        ]);
        return (
          <Card key={id} variant="outlined">
            <CardContent>
              <Stack gap={1}>
                <Text variant="body2">
                  Triggering on all {kind} events for{' '}
                  <OrganizationLabel organizationIds={[organizationId]} /> that match:
                </Text>
                <Box component="ul" paddingLeft={2}>
                  {Object.entries(santizedTrigger).map(([key, value]) => {
                    return (
                      <li key={key}>
                        <Text variant="body2">
                          {_.startCase(key)}: {value || 'Any'}
                        </Text>
                      </li>
                    );
                  })}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        );
      })}
      <Divider />
    </Stack>
  );
}
